import React, { useMemo, useCallback } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { useParams } from 'react-router';
import { Box } from '@mui/material';
import DialogGlynt from '../common/Dialog/DialogGlynt';
import DialogTitleGlynt from '../common/Dialog/DialogTitleGlynt';
import DialogActionsGlynt from '../common/Dialog/DialogActionsGlynt';
import ValidationErrorsTable, { ValidationErrorRow } from '../common/ValidationErrorsTable';
import {
  markValidationByDocumentAtom,
  showMarkValidationErrorsDialogAtom,
} from '../../atoms/mark-validation-atoms';

const ViewMarkValidationErrors: React.FC = () => {
  const [showDialog, setShowDialog] = useAtom(showMarkValidationErrorsDialogAtom);
  const validationByDocument = useAtomValue(markValidationByDocumentAtom);
  const { dpid, id: trainingSetId } = useParams<{ dpid: string; id: string }>();

  const handleClose = () => {
    setShowDialog(false);
  };

  // Generate link to mark page for a specific document
  const getDocumentLink = useCallback(
    (documentId: string) => {
      return `/data-pools/${dpid}/train/${trainingSetId}/mark/${documentId}`;
    },
    [dpid, trainingSetId]
  );

  // Get all validation errors from all documents, excluding dismissed errors
  const allValidationErrors: ValidationErrorRow[] = useMemo(() => {
    return validationByDocument.flatMap((doc) =>
      doc.validationErrorDetails
        .filter((error) => !error.dismissalReason) // Exclude dismissed errors
        .map((error) => ({
          entityId: doc.documentId,
          field: error.field,
          type: error.type,
          error: error.error,
          errorDetail: error.errorDetail,
          dismissalReason: error.dismissalReason,
        }))
    );
  }, [validationByDocument]);

  return (
    <DialogGlynt
      open={showDialog}
      closeModal={handleClose}
      cypressId="mark-validation-errors-dialog"
      width="800px"
    >
      <DialogTitleGlynt title="All Validation Errors" closeModal={handleClose} />

      <Box sx={{ p: 2, flex: 1, maxHeight: '60vh', overflow: 'auto' }}>
        <ValidationErrorsTable
          errors={allValidationErrors}
          entityIdLabel="Document ID"
          showDatasetColumn={false}
          showDismissalColumn={false}
          getEntityLink={getDocumentLink}
        />
      </Box>

      <DialogActionsGlynt
        handleSubmit={handleClose}
        closeModal={handleClose}
        showClearButton={false}
        cancelCypressId="btn-mark-validation-errors-cancel"
        submitCypressId="btn-mark-validation-errors-close"
        clearCypressId="btn-mark-validation-errors-clear"
        submitLoading={false}
        showCancelButton={false}
        submitButtonText="Close"
      />
    </DialogGlynt>
  );
};

export default ViewMarkValidationErrors;
